import { useEffect, useRef, useState } from "react";
import { formatArabicNumber } from "@/lib/formatters";

const DURATION_MS = 900;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface UseCountUpOptions {
  target: number | undefined;
  enabled?: boolean;
}

export function useCountUp({
  target,
  enabled = true,
}: UseCountUpOptions): string {
  const [displayValue, setDisplayValue] = useState(0);
  const currentRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || target === undefined) return;

    if (prefersReducedMotion()) {
      currentRef.current = target;
      setDisplayValue(target);
      return;
    }

    const startValue = currentRef.current;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION_MS, 1);
      const eased = easeOutCubic(progress);
      const next = Math.round(startValue + (target - startValue) * eased);

      currentRef.current = next;
      setDisplayValue(next);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        currentRef.current = target;
        setDisplayValue(target);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, enabled]);

  if (!enabled || target === undefined) {
    return "—";
  }

  return formatArabicNumber(displayValue);
}
